/**
 * Front URL 配置发现核心。
 *
 * 前台按单次请求超时验证候选并一次性发布完整配置；后台只保存下次启动候选。
 * 本模块不依赖框架、Axios、项目密钥或全局业务状态，由 getFrontUrl 适配器注入。
 * 五个独立仓库应同步维护本文件，项目差异只放在 profile 中。
 */

/** 站点或服务地址映射；字段名与各项目现有 window 消费方保持一致。 */
export type FrontUrlMap = Record<string, string>

/** 一次发现得到的局部快照，在校验和持久化完成后才允许发布。 */
export interface FrontUrlSnapshot {
  /** 已通过探活的服务地址，以及从链服务地址派生的 WebSocket 地址。 */
  api: FrontUrlMap
  /** 站点配置；浏览器和邀请页的生产流程可以不提供。 */
  sites?: FrontUrlMap
}

/** 发现请求的取消信号和单次超时，适配器必须传递给实际 HTTP 客户端。 */
export interface FrontUrlRequestOptions {
  /** 单次超时、旧轮失效或探针竞速结束时取消请求。 */
  signal: AbortSignal
  /** 单位为毫秒；配置下载、探活和字典请求均独立等待最多 15 秒。 */
  timeout: number
  /** 配置源和探针按文本读取，避免客户端提前把加密内容解析成 JSON。 */
  responseType?: 'text'
}

/** 一个独立服务的字段映射与启动要求。 */
export interface FrontUrlService {
  /** 输出字段，同时用于读取活动缓存；禁止为读写分别配置不同字段名。 */
  key: string
  /** 解密后 JSON 中的候选数组字段，例如 admin、chain、gcChain 或 s3Link。 */
  jsonKey: string
  /** 缓存不可用时的构建环境候选，缺省表示没有环境兜底。 */
  envUrl?: string
  /** true 表示启动必须得到有效地址，缺省为可选服务。 */
  required?: boolean
  /** 配置后将 http/https 链地址派生为 ws/wss，并保存到指定输出字段。 */
  websocketKey?: string
}

/** 项目适配配置；实例创建后不应再修改这些字段。 */
export interface FrontUrlProfile {
  /** 候选缓存作用域，由项目名、VITE_NODE_ENV 和两类环境 ID 拼接。 */
  cacheScope: string
  /** 仅 VITE_NODE_ENV 严格等于 production 时为 true。 */
  isProduction: boolean
  /** 非生产环境配置地址，包含 frontUrlGc.json 或 frontUrlGea.json。 */
  settingsUrl: string
  /** 非生产配置数组中的站点记录 ID。 */
  urlId: string
  /** 非生产配置数组中的 API 记录 ID，来自大小写敏感的 VITE_Front_API_ID。 */
  apiId: string
  /** 按优先级排列的加密 JSON 源；未配置、无效和失败的源会被跳过。 */
  sources: (string | undefined)[]
  /** 可选的加密文本指针源，解密后得到另一个加密 JSON 的 HTTP(S) 地址。 */
  fallbackUrl?: string
  /** 所需服务及字段映射；资源地址可选，业务核心服务必须明确 required。 */
  services: FrontUrlService[]
  /**
   * 使用候选网关获取站点配置。
   * @param api 已验证的候选服务地址映射。
   * @param options 请求的取消信号和单次超时配置。
   * @returns 站点地址映射。
   */
  loadSites?: (api: FrontUrlMap, options: FrontUrlRequestOptions) => Promise<FrontUrlMap>
  /** 站点字典失败或单次超时时使用的环境站点；整轮取消时禁止降级。 */
  fallbackSites?: FrontUrlMap
}

/** 可替换的 IO 依赖，测试通过注入存储、时钟和请求避免真实网络与等待。 */
export interface FrontUrlDependencies {
  /**
   * 按取消信号和超时配置发起 HTTP 请求。
   * @param url 请求地址。
   * @param options 请求的取消信号、超时和响应类型配置。
   * @returns HTTP 响应体。
   */
  request: (url: string, options: FrontUrlRequestOptions) => Promise<unknown>
  /**
   * 使用当前项目的密钥解密配置文本。
   * @param data 待解密的配置文本。
   * @returns 解密后的文本。
   */
  decrypt: (data: string) => string
  /**
   * 同步发布配置快照，后台刷新不调用此方法。
   * @param snapshot 已验证的配置快照。
   * @returns 无返回值。
   */
  publish: (snapshot: FrontUrlSnapshot) => void
  /** 缺省使用 localStorage；只允许操作本模块拥有的三个配置键。 */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  /**
   * 获取与定时器使用相同时间基准的当前时间。
   * @returns 当前时间，单位为毫秒。
   */
  now?: () => number
  /**
   * 创建延迟执行回调的定时器，缺省使用 setTimeout。
   * @param callback 到期后执行的回调。
   * @param timeout 等待时长，单位为毫秒。
   * @returns 定时器句柄。
   */
  setTimer?: (callback: () => void, timeout: number) => unknown
  /**
   * 清理 setTimer 创建的定时器。
   * @param timer 待清理的定时器句柄。
   * @returns 无返回值。
   */
  clearTimer?: (timer: unknown) => void
}

/** 前台发现选项，支持显式恢复和非生产配置注入。 */
export interface FrontUrlStartOptions {
  /** 跳过候选、内存和活动 API 缓存；仍然允许先验证环境候选。 */
  bypassCache?: boolean
  /** 非生产可提供配置数组、JSON 或 Base64 文本；无效数据不回退下载。 */
  developmentData?: string | FrontUrlMap[]
}

/** 核心实例接口；各项目入口可以只导出其现有业务所需的方法。 */
export interface FrontUrlDiscovery {
  /**
   * 启动前台配置发现，普通并发调用复用在途任务。
   * @param options 可选的缓存跳过和非生产配置注入选项。
   * @returns 被新启动取代时等待其结果；成功为 true，其他失效或失败为 false。
   */
  start(options?: FrontUrlStartOptions): Promise<boolean>
  /**
   * 在后台发现配置并写入候选缓存。
   * @returns 候选缓存写入成功时为 true，未执行或失败时为 false。
   */
  refresh(): Promise<boolean>
  /**
   * 使所有在途任务失效，保留当前已发布配置和持久化缓存。
   * @returns 无返回值。
   */
  invalidate(): void
  /**
   * 使在途任务失效并清理活动缓存、当前作用域候选和已发布配置。
   * @returns 无返回值。
   */
  clearCache(): void
}

interface DiscoveryScope {
  generation: number
  signal: AbortSignal
  optionalResults: Map<string, string>
  /**
   * 取消当前作用域中的任务。
   * @param reason 取消原因。
   * @returns 无返回值。
   */
  abort(reason: unknown): void
}

type DiscoveryErrorCode =
  | 'FRONT_URL_CANCELLED'
  | 'FRONT_URL_REQUEST_TIMEOUT'
  | 'FRONT_URL_PROBE_FAILED'
  | 'FRONT_URL_EMPTY_SOURCE'
  | 'FRONT_URL_INVALID_SOURCE'
  | 'FRONT_URL_REQUIRED_SERVICE_MISSING'
  | 'FRONT_URL_INVALID_CONFIG'
  | 'FRONT_URL_ENVIRONMENT_MISSING'

interface DiscoveryError extends Error {
  code: DiscoveryErrorCode
}

/** 每次下载、探活和站点字典的独立超时，不限制整轮累计耗时。 */
const REQUEST_TIMEOUT_MS = 15_000
const API_CACHE_KEY = 'Global_Front_Api'
const SITE_CACHE_KEY = 'Global_Front_Url'

/**
 * 移除 URL 路径末尾的一个斜杠，保留查询参数和片段。
 * @param url 待序列化的 URL 对象。
 * @returns 移除末尾斜杠后的地址字符串。
 */
function serializeBaseUrl(url: URL): string {
  const value = url.toString()
  const suffixIndex = value.search(/[?#]/)
  const base = suffixIndex < 0 ? value : value.slice(0, suffixIndex)
  const suffix = suffixIndex < 0 ? '' : value.slice(suffixIndex)
  return `${base.replace(/\/$/, '')}${suffix}`
}

/**
 * 补齐缺省的 HTTPS 协议并归一化 URL。
 * @param value 待归一化的地址。
 * @returns 有效的 HTTP(S) 地址，空值或无效地址返回空字符串。
 */
function normalizeUrl(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return ''
  try {
    const input = value.trim()
    const hostPort = /^[^/?#:\s]+:\d+(?:[/?#]|$)/.test(input)
    const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(input) && !hostPort
    const url = new URL(hasScheme ? input : `https://${input}`)
    if (!['http:', 'https:'].includes(url.protocol)) return ''
    return serializeBaseUrl(url)
  } catch {
    return ''
  }
}

/**
 * 保留已有查询参数、移除片段并添加随机参数，探针请求追加 /canuse。
 * @param value 有效的 HTTP(S) 地址。
 * @param probe 是否生成探针地址，默认为 false。
 * @returns 带随机查询参数的请求地址。
 */
function requestUrl(value: string, probe = false): string {
  const url = new URL(value)
  if (probe) url.pathname = `${url.pathname.replace(/\/$/, '')}/canuse`
  url.hash = ''
  url.searchParams.set('v', String(Math.random()))
  return url.toString()
}

/**
 * 由链服务地址派生 WebSocket 地址，保留域名、端口和路径。
 * @param value 已验证的 HTTP(S) 链服务地址。
 * @returns 使用 WS 或 WSS 协议的地址。
 */
function websocketUrl(value: string): string {
  const url = new URL(value)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  return serializeBaseUrl(url)
}

/**
 * 创建地址发现流程的内部诊断错误。
 * @param code 错误代码。
 * @returns 包含指定错误代码的诊断错误对象。
 */
function discoveryError(code: DiscoveryErrorCode): DiscoveryError {
  const error = new Error(code) as DiscoveryError
  error.name = 'FrontUrlDiscoveryError'
  error.code = code
  return error
}

/**
 * 为当前页面会话创建地址发现实例，管理前后台任务和配置缓存。
 * @param profile 项目环境、配置源和服务字段映射。
 * @param dependencies 请求、解密、发布以及可选的存储和计时依赖。
 * @returns 提供启动、后台刷新、任务失效和缓存清理方法的发现实例。
 */
export function createFrontUrlDiscovery(
  profile: FrontUrlProfile,
  dependencies: FrontUrlDependencies,
): FrontUrlDiscovery {
  const now = dependencies.now ?? Date.now
  const schedule = dependencies.setTimer ?? setTimeout
  const unschedule = dependencies.clearTimer ?? clearTimeout
  const storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = dependencies.storage ?? {
    getItem: (key) => globalThis.localStorage.getItem(key),
    setItem: (key, value) => globalThis.localStorage.setItem(key, value),
    removeItem: (key) => globalThis.localStorage.removeItem(key),
  }
  const candidateKey = `Global_Front_Candidate:${profile.cacheScope}`
  const scopes = new Set<DiscoveryScope>()
  let generation = 0
  let active: FrontUrlSnapshot | null = null
  let startup: Promise<boolean> | null = null
  let replaceStartup: ((replacement: Promise<boolean>) => void) | null = null
  let background: Promise<boolean> | null = null

  /**
   * 读取并解析对象缓存，忽略损坏、类型不符或不可读取的缓存。
   * @param key 缓存键。
   * @returns 解析后的对象，缓存未命中或无效时返回 null。
   */
  function read(key: string): Record<string, unknown> | null {
    try {
      const value = JSON.parse(storage.getItem(key) || 'null')
      return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }

  /**
   * 检查作用域的取消状态和代次，已失效时抛出错误。
   * @param scope 当前发现任务的作用域。
   * @returns 无返回值。
   */
  function assertCurrent(scope: DiscoveryScope): void {
    if (scope.signal.aborted) throw scope.signal.reason
    if (scope.generation !== generation) throw discoveryError('FRONT_URL_CANCELLED')
  }

  /**
   * 在独立的可取消作用域中执行操作，完成后取消剩余子请求。
   * @param operation 接收当前作用域并执行异步任务的回调。
   * @returns 异步操作的执行结果。
   */
  async function run<T>(operation: (scope: DiscoveryScope) => Promise<T>): Promise<T> {
    const controller = new AbortController()
    const scope: DiscoveryScope = {
      generation,
      signal: controller.signal,
      optionalResults: new Map<string, string>(),
      abort: (reason) => controller.abort(reason),
    }
    scopes.add(scope)
    let onAbort!: () => void
    const cancelled = new Promise<never>((_, reject) => {
      onAbort = () => reject(controller.signal.reason)
      controller.signal.addEventListener('abort', onAbort, { once: true })
    })
    try {
      return await Promise.race([operation(scope), cancelled])
    } finally {
      controller.signal.removeEventListener('abort', onAbort)
      controller.abort(discoveryError('FRONT_URL_CANCELLED'))
      scopes.delete(scope)
    }
  }

  /**
   * 为网络操作设置独立的 15 秒超时，并响应父级取消信号。
   * @param scope 当前发现任务的作用域。
   * @param operation 接收取消信号和超时配置的异步操作。
   * @param parentSignal 父级取消信号，默认使用当前作用域的信号。
   * @returns 网络操作的执行结果。
   */
  async function withRequestTimeout<T>(
    scope: DiscoveryScope,
    operation: (options: FrontUrlRequestOptions) => Promise<T>,
    parentSignal = scope.signal,
  ): Promise<T> {
    assertCurrent(scope)
    if (parentSignal.aborted) throw parentSignal.reason
    const controller = new AbortController()
    const deadline = now() + REQUEST_TIMEOUT_MS
    const onParentAbort = () => controller.abort(parentSignal.reason)
    parentSignal.addEventListener('abort', onParentAbort, { once: true })
    const timer = schedule(
      () => controller.abort(discoveryError('FRONT_URL_REQUEST_TIMEOUT')),
      REQUEST_TIMEOUT_MS,
    )
    let onAbort!: () => void
    const cancelled = new Promise<never>((_, reject) => {
      onAbort = () => reject(controller.signal.reason)
      controller.signal.addEventListener('abort', onAbort, { once: true })
    })
    try {
      const result = await Promise.race([
        operation({
          signal: controller.signal,
          timeout: REQUEST_TIMEOUT_MS,
        }),
        cancelled,
      ])
      assertCurrent(scope)
      if (now() >= deadline) throw discoveryError('FRONT_URL_REQUEST_TIMEOUT')
      return result
    } finally {
      unschedule(timer as ReturnType<typeof setTimeout>)
      parentSignal.removeEventListener('abort', onParentAbort)
      controller.signal.removeEventListener('abort', onAbort)
      controller.abort(discoveryError('FRONT_URL_CANCELLED'))
    }
  }

  /**
   * 在独立超时和取消控制下按文本请求数据。
   * @param scope 当前发现任务的作用域。
   * @param url 请求地址。
   * @param parentSignal 父级取消信号，默认使用当前作用域的信号。
   * @returns HTTP 响应体。
   */
  function request(
    scope: DiscoveryScope,
    url: string,
    parentSignal = scope.signal,
  ): Promise<unknown> {
    return withRequestTimeout(
      scope,
      (options) => dependencies.request(url, { ...options, responseType: 'text' }),
      parentSignal,
    )
  }

  /**
   * 并发探活候选地址，接受首个响应体严格等于 success 的地址并取消其余探针。
   * @param scope 当前发现任务的作用域。
   * @param candidates 待归一化并探活的候选地址列表。
   * @returns 首个探活成功的地址，全部失败时返回空字符串。
   */
  async function probe(scope: DiscoveryScope, candidates: unknown[]): Promise<string> {
    // 服务地址将用于 baseURL 和路径拼接；配置下载地址仍允许查询串和片段。
    const urls = [
      ...new Set(candidates.map(normalizeUrl).filter((url) => url && !/[?#]/.test(url))),
    ]
    if (!urls.length) return ''
    const controller = new AbortController()
    const onAbort = () => controller.abort(scope.signal.reason)
    scope.signal.addEventListener('abort', onAbort, { once: true })
    try {
      return await Promise.any(
        urls.map(async (url) => {
          const data = await request(scope, requestUrl(url, true), controller.signal)
          if (data !== 'success') throw discoveryError('FRONT_URL_PROBE_FAILED')
          return url
        }),
      )
    } catch {
      assertCurrent(scope)
      return ''
    } finally {
      scope.signal.removeEventListener('abort', onAbort)
      controller.abort(discoveryError('FRONT_URL_CANCELLED'))
    }
  }

  /**
   * 检查所有必需服务是否已有有效地址。
   * @param api 当前服务地址映射。
   * @returns 必需服务齐全时为 true，否则为 false。
   */
  const complete = (api: FrontUrlMap): boolean =>
    profile.services.every((service) => !service.required || api[service.key])

  /**
   * 探活并补齐缺失服务，保留已有地址，可选结果在快照发布前统一采纳。
   * @param scope 当前发现任务的作用域。
   * @param api 待补齐的服务地址映射。
   * @param getCandidates 根据服务配置获取候选地址的回调。
   * @param collectOptional 是否等待全部可选服务，默认为 false。
   * @returns 服务地址补齐任务完成后的 Promise，无返回值。
   */
  async function fill(
    scope: DiscoveryScope,
    api: FrontUrlMap,
    getCandidates: (service: FrontUrlService) => unknown,
    collectOptional = false,
  ): Promise<void> {
    const results = new Map<string, string>()
    const tasks = profile.services.map((service) => {
      const task = (async () => {
        if (api[service.key]) return
        const candidates = getCandidates(service)
        const value = await probe(scope, Array.isArray(candidates) ? candidates : [])
        results.set(service.key, value)
        if (!service.required && value && !scope.optionalResults.has(service.key)) {
          scope.optionalResults.set(service.key, value)
        }
      })()
      // 前台只等待必需服务；可选探针只写本轮局部结果，不得迟到修改已发布对象。
      if (!service.required && !collectOptional) void task.catch(() => {})
      return { service, task }
    })
    await Promise.all(
      tasks.filter(({ service }) => collectOptional || service.required).map(({ task }) => task),
    )
    assertCurrent(scope)
    for (const service of profile.services) {
      api[service.key] = api[service.key] || results.get(service.key) || ''
    }
  }

  /**
   * 拉取并解密单个 JSON 配置源，探活并补齐服务，失败时保留已有结果。
   * @param scope 当前发现任务的作用域。
   * @param source 待归一化的配置源地址。
   * @param api 待补齐的服务地址映射。
   * @param collectOptional 是否等待全部可选服务。
   * @returns 配置源处理完成后的 Promise，无返回值。
   */
  async function fromSource(
    scope: DiscoveryScope,
    source: unknown,
    api: FrontUrlMap,
    collectOptional: boolean,
  ): Promise<void> {
    const url = normalizeUrl(source)
    if (!url) return
    try {
      const data = await request(scope, requestUrl(url))
      if (typeof data !== 'string' || !data.trim()) throw discoveryError('FRONT_URL_EMPTY_SOURCE')
      const decoded = JSON.parse(dependencies.decrypt(data.trim())) as Record<string, unknown>
      await fill(scope, api, (service) => decoded[service.jsonKey], collectOptional)
    } catch {
      assertCurrent(scope)
    }
  }

  /**
   * 按优先级遍历加密配置源，服务不足时使用解密后的兜底指针继续发现。
   * @param scope 当前发现任务的作用域。
   * @param api 待补齐的服务地址映射。
   * @param collectOptional 是否继续收集全部可选服务，默认为 false。
   * @returns 远端配置发现完成后的 Promise，无返回值。
   */
  async function remote(
    scope: DiscoveryScope,
    api: FrontUrlMap,
    collectOptional = false,
  ): Promise<void> {
    const enough = () =>
      collectOptional ? profile.services.every((service) => api[service.key]) : complete(api)
    for (const source of profile.sources) {
      await fromSource(scope, source, api, collectOptional)
      if (enough()) return
    }
    const fallbackUrl = normalizeUrl(profile.fallbackUrl)
    if (fallbackUrl && !enough()) {
      try {
        const data = await request(scope, requestUrl(fallbackUrl))
        if (typeof data !== 'string') throw discoveryError('FRONT_URL_EMPTY_SOURCE')
        const decoded = dependencies.decrypt(data.trim()).trim()
        if (!/^https?:\/\//i.test(decoded)) throw discoveryError('FRONT_URL_INVALID_SOURCE')
        await fromSource(scope, decoded, api, collectOptional)
      } catch {
        assertCurrent(scope)
      }
    }
  }

  /**
   * 校验必需服务、派生 WebSocket 地址并获取站点，生成待提交的配置快照。
   * @param scope 当前发现任务的作用域。
   * @param api 已探活的服务地址映射。
   * @returns 包含 API 和可选站点地址的配置快照。
   */
  async function snapshot(scope: DiscoveryScope, api: FrontUrlMap): Promise<FrontUrlSnapshot> {
    if (!complete(api)) throw discoveryError('FRONT_URL_REQUIRED_SERVICE_MISSING')
    for (const service of profile.services) {
      if (service.websocketKey && api[service.key]) {
        api[service.websocketKey] = websocketUrl(api[service.key])
      }
    }
    let sites: FrontUrlMap | undefined
    const loadSites = profile.loadSites
    if (loadSites) {
      try {
        sites = await withRequestTimeout(scope, (options) => loadSites(api, options))
      } catch (error) {
        assertCurrent(scope)
        if (!profile.fallbackSites) throw error
        sites = { ...profile.fallbackSites }
      }
    }
    assertCurrent(scope)
    // 字典获取期间已完成的可选探针可随配置一起发布，发布后不再接收迟到结果。
    for (const service of profile.services) {
      if (!service.required && !api[service.key]) {
        api[service.key] = scope.optionalResults.get(service.key) || ''
      }
    }
    return { api, ...(sites ? { sites } : {}) }
  }

  /**
   * 依次验证候选缓存、活动缓存、环境候选和远端源，获取生产环境配置。
   * @param scope 当前发现任务的作用域。
   * @param bypassCache 是否跳过候选、内存和活动 API 缓存。
   * @returns 配置快照及是否通过本地候选补齐全部必需服务的标记。
   */
  async function production(
    scope: DiscoveryScope,
    bypassCache: boolean | undefined,
  ): Promise<{ value: FrontUrlSnapshot; usedLocal: boolean }> {
    const api: FrontUrlMap = {}
    if (!bypassCache) {
      // 同一次页面会话不消费后台候选；只有重新启动的新实例才读取它。
      const candidate = active ? null : read(candidateKey)
      if (candidate?.version === 1 && candidate.scope === profile.cacheScope && candidate.api) {
        await fill(scope, api, (service) => [
          (candidate.api as Record<string, unknown>)[service.key],
        ])
      }
      if (!complete(api)) {
        const cached = active?.api ?? read(API_CACHE_KEY) ?? {}
        await fill(scope, api, (service) => [cached[service.key]])
      }
    }
    // 缓存探活失败后继续验证环境候选，不能被缓存的存在短路。
    if (profile.services.some((service) => !api[service.key])) {
      await fill(scope, api, (service) => [service.envUrl])
    }
    const usedLocal = complete(api)
    if (!usedLocal) await remote(scope, api)
    return { value: await snapshot(scope, api), usedLocal }
  }

  /**
   * 读取配置数组、JSON 或 Base64 文本，按站点和 API 的 ID 获取非生产配置。
   * @param scope 当前发现任务的作用域。
   * @param options 包含可选配置数组或文本的启动选项。
   * @returns 配置快照及固定为 false 的本地候选命中标记。
   */
  async function development(
    scope: DiscoveryScope,
    options: FrontUrlStartOptions,
  ): Promise<{ value: FrontUrlSnapshot; usedLocal: boolean }> {
    let data: unknown = options.developmentData
    if (!Object.prototype.hasOwnProperty.call(options, 'developmentData')) {
      const url = normalizeUrl(profile.settingsUrl)
      if (!url) throw discoveryError('FRONT_URL_INVALID_SOURCE')
      data = await request(scope, requestUrl(url))
    }
    let records = data as FrontUrlMap[]
    if (typeof data === 'string') {
      try {
        records = JSON.parse(data)
      } catch {
        records = JSON.parse(decodeURIComponent(atob(data)))
      }
    }
    if (!Array.isArray(records)) throw discoveryError('FRONT_URL_INVALID_CONFIG')
    const sites = records.find((record) => record.id === profile.urlId)
    const api = records.find((record) => record.id === profile.apiId)
    if (!sites || !api) throw discoveryError('FRONT_URL_ENVIRONMENT_MISSING')
    assertCurrent(scope)
    return { value: { api, sites }, usedLocal: false }
  }

  /**
   * 同步持久化活动配置，写入失败时尽力回滚并抛出错误。
   * @param value 待持久化的配置快照。
   * @returns 无返回值。
   */
  function persist(value: FrontUrlSnapshot): void {
    const entries: [string, string][] = [[API_CACHE_KEY, JSON.stringify(value.api)]]
    if (value.sites) entries.push([SITE_CACHE_KEY, JSON.stringify(value.sites)])
    const previous = entries.map(([key]): [string, string | null] => [key, storage.getItem(key)])
    try {
      for (const [key, data] of entries) storage.setItem(key, data)
    } catch (error) {
      for (const [key, data] of previous) {
        try {
          if (data === null) storage.removeItem(key)
          else storage.setItem(key, data)
        } catch {
          // Storage 无事务；回滚失败时尽量移除半份配置。
          try {
            storage.removeItem(key)
          } catch {
            /* Storage 不可用。 */
          }
        }
      }
      throw error
    }
  }

  /**
   * 使所有在途任务失效，保留已发布配置和持久化缓存。
   * @returns 无返回值。
   */
  function invalidate(): void {
    generation += 1
    for (const scope of scopes) scope.abort(discoveryError('FRONT_URL_CANCELLED'))
    startup = null
    replaceStartup = null
    background = null
  }

  /**
   * 合并并发后台刷新，将发现的完整配置写入当前作用域的候选缓存。
   * @returns 候选缓存写入成功时为 true，未执行、取消或失败时为 false。
   */
  function refresh(): Promise<boolean> {
    if (!profile.isProduction || !active) return Promise.resolve(false)
    if (background) return background
    const task = run(async (scope) => {
      const api: FrontUrlMap = {}
      await remote(scope, api, true)
      if (
        complete(api) &&
        profile.services.some((service) => !service.required && !api[service.key])
      ) {
        await fill(scope, api, (service) => [service.envUrl], true)
      }
      const value = await snapshot(scope, api)
      assertCurrent(scope)
      // 候选整体写入一个 key；不修改活动缓存、window 或当前会话的请求地址。
      storage.setItem(
        candidateKey,
        JSON.stringify({ version: 1, scope: profile.cacheScope, ...value }),
      )
      return true
    }).catch(() => false)
    background = task
    void task.then(() => {
      if (background === task) background = null
    })
    return task
  }

  /**
   * 启动前台配置发现并发布快照，本地命中或可选服务缺失时触发后台刷新。
   * @param options 缓存跳过和非生产配置注入选项，默认为空对象。
   * @returns 被新启动取代时等待其结果；成功为 true，其他失效或失败为 false。
   */
  function start(options: FrontUrlStartOptions = {}): Promise<boolean> {
    const hasDevelopmentData =
      !profile.isProduction && Object.prototype.hasOwnProperty.call(options, 'developmentData')
    if (startup && !options.bypassCache && !hasDevelopmentData) return startup
    const replacePrevious = replaceStartup
    invalidate()
    let replacement: Promise<boolean> | null = null
    const task = run(async (scope) => {
      const { value, usedLocal } = profile.isProduction
        ? await production(scope, options.bypassCache)
        : await development(scope, options)
      assertCurrent(scope)
      if (profile.isProduction) persist(value)
      dependencies.publish(value)
      active = value
      const missingOptional = profile.services.some(
        (service) => !service.required && !value.api[service.key],
      )
      if (usedLocal || missingOptional) void refresh()
      return true
    }).catch(() => replacement ?? false)
    startup = task
    replaceStartup = (next) => {
      replacement = next
    }
    replacePrevious?.(task)
    void task.then(() => {
      if (startup === task) {
        startup = null
        replaceStartup = null
      }
    })
    return task
  }

  /**
   * 使在途任务失效，清理当前模块的活动缓存和候选缓存，并发布空配置。
   * @returns 无返回值。
   */
  function clearCache(): void {
    invalidate()
    active = null
    for (const key of [API_CACHE_KEY, SITE_CACHE_KEY, candidateKey]) {
      try {
        storage.removeItem(key)
      } catch {
        /* 保持内存清理可继续执行。 */
      }
    }
    dependencies.publish({ api: {}, sites: {} })
  }

  return { start, refresh, invalidate, clearCache }
}
